import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useBreadcrumbActions,
  useBreadcrumbContributions,
} from "./BreadcrumbProvider";
import { buildTrail, parentCrumbUrl } from "./buildTrail";
import { buildRouteUrl, stripTrailingSlash } from "./routeUrl";
import { linkMatches } from "./sidebarTrail";

// The URL a crumb is keyed by: the current page's, or with `routePath` an ancestor's,
// built from the current URL's params exactly as the trail builds it.
function useCrumbUrl(routePath) {
  const params = useParams();
  const currentPath = stripTrailingSlash(useLocation().pathname);
  return routePath
    ? buildRouteUrl(routePath, { ...params, type: currentPath.split("/")[1] })
    : currentPath;
}

/**
 * Names a crumb with the record the page has loaded ("Tower 7" instead of "#12").
 * Pass `routePath` to name an ancestor instead of the page itself — an edit form
 * naming its record: `useBreadcrumbLabel(customer?.name, "customers/:id")`.
 * The path is written exactly as it is declared in routes.js.
 */
export function useBreadcrumbLabel(label, routePath) {
  const actions = useBreadcrumbActions();
  const url = useCrumbUrl(routePath);

  useEffect(() => {
    if (!actions || !url || !label) return undefined;
    actions.setLabel(url, label);
    return () => actions.clearLabel(url, label);
  }, [actions, url, label]);
}

/**
 * Adds one crumb after the page for a view that lives in the query string, such as
 * a settings tab: Settings › General Settings. The page's own crumb then links back
 * to the page without the query.
 */
export function useBreadcrumbTab(label) {
  const actions = useBreadcrumbActions();
  const { pathname } = useLocation();
  const url = stripTrailingSlash(pathname);
  const tab = useMemo(() => (label ? { url, label } : null), [url, label]);

  useEffect(() => {
    if (!actions || !tab) return undefined;
    actions.setTab(tab);
    return () => actions.clearTab(tab);
  }, [actions, tab]);
}

/**
 * Replaces the parent declared in routes.js when only the loaded record knows it —
 * a stock voucher's list depends on the voucher's type. `parentPath` is a path
 * declared in routes.js (a query after it stays on the link); `params` fills the
 * route params the current URL lacks or names differently, and the levels above
 * inherit them. Pass `routePath` to replace an ancestor's parent instead of the
 * page's own — an edit form re-parenting its record.
 */
export function useBreadcrumbParent(parentPath, params, routePath) {
  const actions = useBreadcrumbActions();
  const url = useCrumbUrl(routePath);
  // Callers pass a new object on every render, so compare the params by value.
  const paramsKey = JSON.stringify(params ?? {});
  const parent = useMemo(
    () => (parentPath ? { path: parentPath, params: params ?? {} } : null),
    [parentPath, paramsKey]
  );

  useEffect(() => {
    if (!actions || !url || !parent) return undefined;
    actions.setParent(url, parent);
    return () => actions.clearParent(url, parent);
  }, [actions, url, parent]);
}

/**
 * Where Back goes: one level up the trail, never the browser's previous entry — after a
 * save that is the form just submitted. A level the user came down from keeps the query
 * it was left on, unless that contradicts the query the trail's link sets (`?view=`).
 * Null when no level above the page may be opened.
 */
export function useBreadcrumbBackUrl() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const contributions = useBreadcrumbContributions();

  return useMemo(() => {
    const crumbs = buildTrail({ pathname, search, contributions, t });
    const url = parentCrumbUrl(crumbs);
    if (!url) return null;

    const [path] = url.split("?");
    const remembered = contributions.queries[path];
    // On a tab of this same page the remembered query is the tab itself.
    if (remembered === undefined || path === stripTrailingSlash(pathname)) {
      return url;
    }
    return linkMatches(url, path, remembered) ? `${path}${remembered}` : url;
  }, [pathname, search, contributions, t]);
}
